import { createPortal } from 'react-dom';
import { ImageCarousel } from '@/components/ImageCarousel';
import { usePageData } from '@/contexts/pageContext';
import { useHasMounted } from '@/hooks/useHasMounted';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getIsBook } from '@/utils/getIsBook';

const imageCarouselPortal = document.querySelector('.product__images');
const mainProductImage = document.querySelector(
  '.product__images__image-container',
);

// One of the limitations of this carousel library is that one has to specify
// the number of items per page instead of having it calculated responsively.
// I've determined several breakpoints in order to specify the optimum number
// of items per page. It's a rather brittle solution but works.
const breakpoints = ['30rem', '36rem', '43rem', '50rem', '58rem'];

const getItemsPerPage = (matches: boolean[]) => {
  if (matches[4]) {
    return 1;
  }

  if (matches[3]) {
    return 2.1;
  }

  if (matches[2]) {
    return 1.65;
  }

  if (matches[1]) {
    return 1.5;
  }

  if (matches[0]) {
    return 1.25;
  }

  return 1.1;
};

export const useImageCarousel = () => {
  const { product } = usePageData();

  const images = product?.carouselImages;
  const hasCarouselImages = (images?.length ?? 0) > 1;

  const promoBadgeProp = product?.promoBadge
    ? { promoBadge: product.promoBadge }
    : {};

  const hasMounted = useHasMounted(() => {
    if (
      imageCarouselPortal &&
      mainProductImage &&
      hasMounted &&
      hasCarouselImages
    ) {
      mainProductImage.parentNode?.removeChild(mainProductImage);
    }
  });

  const lookInsideButton = hasMounted
    ? document.querySelector('.product__look-inside > a')
    : null;
  const offset = lookInsideButton?.clientWidth;

  const breakpointMatches = breakpoints.map(breakpoint =>
    useMediaQuery(`(width >= ${breakpoint})`),
  );

  return imageCarouselPortal && hasCarouselImages && product
    ? createPortal(
        <ImageCarousel
          images={images}
          isBook={getIsBook(product.productType)}
          isWideScreen={breakpointMatches[4]}
          itemsPerPage={getItemsPerPage(breakpointMatches)}
          offset={offset}
          {...promoBadgeProp}
        />,
        imageCarouselPortal,
      )
    : null;
};
